import { useState, useRef, useEffect } from 'react';
import { ProductDetail } from '@/mocks/productDetails';

interface ProductTableProps {
  products: ProductDetail[];
  onEdit: (product: ProductDetail) => void;
  onDelete: (id: number) => void;
  onUpdateStock?: (id: number, stock: number, prevStock: number) => Promise<boolean>;
  onUpdatePrice?: (id: number, price: number, originalPrice: number) => Promise<boolean>;
  onUpdateName?: (id: number, nameAr: string) => Promise<boolean>;
  onUpdateCategory?: (id: number, category: string) => Promise<boolean>;
}

const CATEGORIES: Record<string, string> = {
  PS5: 'بلايستيشن 5',
  PS4: 'بلايستيشن 4',
  PC: 'كمبيوتر',
  Controllers: 'يد تحكم',
  Headsets: 'سماعات',
  Accessories: 'إكسسوارات',
};

/* ─────────────────────────── NameCell ─────────────────────────── */
function NameCell({
  product,
  onUpdateName,
}: {
  product: ProductDetail;
  onUpdateName?: (id: number, nameAr: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(product.nameAr);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    setValue(product.nameAr);
  }, [product.nameAr]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValue(product.nameAr);
      setEditing(false);
      return;
    }
    if (trimmed === product.nameAr) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onUpdateName?.(product.id, trimmed);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } else {
      setValue(product.nameAr);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setValue(product.nameAr);
      setEditing(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {/* Thumbnail */}
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={product.nameAr} className="w-full h-full object-cover object-top" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-image-line text-gray-300 text-lg"></i>
          </div>
        )}
      </div>

      {/* Name area */}
      <div className="min-w-0">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              className="w-44 text-sm font-medium border border-[#1a1a1a] rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1a1a1a]"
              dir="rtl"
            />
            <button
              onMouseDown={e => { e.preventDefault(); handleSave(); }}
              className="w-6 h-6 flex items-center justify-center rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer flex-shrink-0"
              title="حفظ"
            >
              <i className="ri-check-line text-xs"></i>
            </button>
            <button
              onMouseDown={e => { e.preventDefault(); setValue(product.nameAr); setEditing(false); }}
              className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0"
              title="إلغاء"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 group/name">
            {saving ? (
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <i className="ri-loader-4-line animate-spin text-xs"></i>
                جاري الحفظ...
              </span>
            ) : saved ? (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                <i className="ri-check-line text-xs"></i>
                تم الحفظ
              </span>
            ) : (
              <p className="font-medium text-[#1a1a1a] text-sm truncate max-w-[180px]">{product.nameAr}</p>
            )}
            {onUpdateName && !saving && !saved && (
              <button
                onClick={() => setEditing(true)}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer opacity-0 group-hover/name:opacity-100 flex-shrink-0"
                title="تعديل الاسم"
              >
                <i className="ri-pencil-line text-xs"></i>
              </button>
            )}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-0.5 line-clamp-1 max-w-[180px]">{product.shortDesc}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────── CategoryCell ─────────────────────────── */
const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  PS5:         { bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200' },
  PS4:         { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200' },
  PC:          { bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200'    },
  Controllers: { bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200'   },
  Headsets:    { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200'   },
  Accessories: { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200'  },
};

function CategoryCell({
  product,
  onUpdateCategory,
}: {
  product: ProductDetail;
  onUpdateCategory?: (id: number, category: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = async (cat: string) => {
    setOpen(false);
    if (cat === product.category) return;
    setSaving(true);
    const ok = await onUpdateCategory?.(product.id, cat);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
  };

  const colors = CATEGORY_COLORS[product.category] ?? { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };

  return (
    <div className="relative flex items-center gap-1.5 group/cat" ref={dropdownRef}>
      {saving ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md border bg-gray-50 text-gray-400 border-gray-200">
          <i className="ri-loader-4-line animate-spin text-xs"></i>
          جاري الحفظ...
        </span>
      ) : saved ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border bg-emerald-50 text-emerald-600 border-emerald-200">
          <i className="ri-check-line text-xs"></i>
          تم الحفظ
        </span>
      ) : (
        <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-md border ${colors.bg} ${colors.text} ${colors.border}`}>
          {CATEGORIES[product.category] || product.category}
        </span>
      )}

      {onUpdateCategory && !saving && !saved && (
        <button
          onClick={() => setOpen(v => !v)}
          className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer opacity-0 group-hover/cat:opacity-100 flex-shrink-0"
          title="تغيير الفئة"
        >
          <i className="ri-pencil-line text-xs"></i>
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[140px]">
          {Object.entries(CATEGORIES).map(([key, label]) => {
            const c = CATEGORY_COLORS[key] ?? { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };
            const isActive = key === product.category;
            return (
              <button
                key={key}
                onClick={() => handleSelect(key)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-right hover:bg-gray-50 transition-colors cursor-pointer ${isActive ? 'bg-gray-50' : ''}`}
                dir="rtl"
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.bg.replace('bg-', 'bg-').replace('-50', '-400')}`}
                  style={{ background: isActive ? 'currentColor' : undefined }}
                ></span>
                <span className={`flex-1 font-medium ${isActive ? c.text : 'text-gray-600'}`}>{label}</span>
                {isActive && <i className="ri-check-line text-xs text-emerald-500"></i>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── StockCell ─────────────────────────── */
function StockCell({
  product,
  onUpdateStock,
}: {
  product: ProductDetail;
  onUpdateStock?: (id: number, stock: number, prevStock: number) => Promise<boolean>;
}) {
  const [value, setValue] = useState(product.stock ?? 0);
  const [inputValue, setInputValue] = useState(String(product.stock ?? 0));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pendingValue, setPendingValue] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track the last saved value to avoid overwriting local state with stale props
  const lastSavedRef = useRef<number>(product.stock ?? 0);

  // Sync when product.stock changes from outside (e.g. parent refetch),
  // but only if it's a genuinely new value (not our own save echoing back)
  useEffect(() => {
    const incoming = product.stock ?? 0;
    if (incoming !== lastSavedRef.current) {
      lastSavedRef.current = incoming;
      setValue(incoming);
      setInputValue(String(incoming));
      setPendingValue(null);
    }
  }, [product.stock]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const currentStock = value;
  const displayStock = pendingValue !== null ? pendingValue : currentStock;

  const stockBadge =
    currentStock === 0
      ? { label: 'نفد', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-400' }
      : currentStock <= 5
      ? { label: `${currentStock} قطع`, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' }
      : { label: `${currentStock}`, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-400' };

  const handleIncrement = () => {
    const base = pendingValue !== null ? pendingValue : currentStock;
    const next = base + 1;
    setPendingValue(next);
    setInputValue(String(next));
    setEditing(true);
  };

  const handleDecrement = () => {
    const base = pendingValue !== null ? pendingValue : currentStock;
    const next = Math.max(0, base - 1);
    setPendingValue(next);
    setInputValue(String(next));
    setEditing(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const parsed = parseInt(e.target.value, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      setPendingValue(parsed);
    }
  };

  const handleSave = async () => {
    if (pendingValue === null || pendingValue === currentStock) {
      setPendingValue(null);
      setEditing(false);
      return;
    }
    if (pendingValue < 0) {
      setPendingValue(null);
      setEditing(false);
      return;
    }
    setSaving(true);
    const prevStock = currentStock;
    const targetValue = pendingValue;
    const ok = await onUpdateStock?.(product.id, targetValue, prevStock);
    setSaving(false);
    if (ok) {
      // Update local state and mark as saved so the useEffect doesn't overwrite it
      lastSavedRef.current = targetValue;
      setValue(targetValue);
      setInputValue(String(targetValue));
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
    setPendingValue(null);
    setEditing(false);
  };

  const handleCancel = () => {
    setPendingValue(null);
    setInputValue(String(currentStock));
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  const hasPending = pendingValue !== null && pendingValue !== currentStock;

  if (saving) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border bg-gray-50 text-gray-400 border-gray-200">
        <i className="ri-loader-4-line animate-spin text-xs"></i>
        جاري الحفظ...
      </span>
    );
  }

  if (saved) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border bg-emerald-50 text-emerald-600 border-emerald-200">
        <i className="ri-check-line text-xs"></i>
        تم الحفظ
      </span>
    );
  }

  if (editing || hasPending) {
    return (
      <div className="flex flex-col gap-1.5">
        {/* +/- controls */}
        <div className="flex items-center gap-1">
          <button
            onMouseDown={e => { e.preventDefault(); handleDecrement(); }}
            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0 text-sm font-bold"
          >
            −
          </button>
          <input
            ref={inputRef}
            type="number"
            min="0"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-14 text-xs font-semibold text-center border border-gray-300 rounded-md px-1 py-1 focus:outline-none focus:border-[#1a1a1a] focus:ring-1 focus:ring-[#1a1a1a]"
            dir="ltr"
          />
          <button
            onMouseDown={e => { e.preventDefault(); handleIncrement(); }}
            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0 text-sm font-bold"
          >
            +
          </button>
        </div>
        {/* Save / Cancel */}
        {hasPending && (
          <div className="flex items-center gap-1">
            <button
              onMouseDown={e => { e.preventDefault(); handleSave(); }}
              className="flex-1 flex items-center justify-center gap-1 text-[10px] font-bold py-1 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line text-xs"></i>
              حفظ ({displayStock})
            </button>
            <button
              onMouseDown={e => { e.preventDefault(); handleCancel(); }}
              className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group/stock">
      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border ${stockBadge.bg} ${stockBadge.text} ${stockBadge.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${stockBadge.dot} ${currentStock <= 5 && currentStock > 0 ? 'animate-pulse' : ''}`}></span>
        {stockBadge.label}
      </span>
      {onUpdateStock && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover/stock:opacity-100 transition-opacity">
          <button
            onMouseDown={e => { e.preventDefault(); handleDecrement(); }}
            className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer text-xs font-bold"
            title="تقليل"
          >
            −
          </button>
          <button
            onMouseDown={e => { e.preventDefault(); setEditing(true); }}
            className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            title="تعديل المخزون"
          >
            <i className="ri-pencil-line text-xs"></i>
          </button>
          <button
            onMouseDown={e => { e.preventDefault(); handleIncrement(); }}
            className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer text-xs font-bold"
            title="زيادة"
          >
            +
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── PriceCell ─────────────────────────── */
function PriceCell({
  product,
  onUpdatePrice,
}: {
  product: ProductDetail;
  onUpdatePrice?: (id: number, price: number, originalPrice: number) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [priceVal, setPriceVal] = useState(String(product.price));
  const [origVal, setOrigVal] = useState(String(product.originalPrice));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const priceRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && priceRef.current) {
      priceRef.current.focus();
      priceRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    setPriceVal(String(product.price));
    setOrigVal(String(product.originalPrice));
  }, [product.price, product.originalPrice]);

  const handleSave = async () => {
    const newPrice = parseFloat(priceVal);
    const newOrig = parseFloat(origVal);
    if (isNaN(newPrice) || newPrice <= 0) {
      setPriceVal(String(product.price));
      setOrigVal(String(product.originalPrice));
      setEditing(false);
      return;
    }
    const finalOrig = isNaN(newOrig) || newOrig <= 0 ? newPrice : newOrig;
    if (newPrice === product.price && finalOrig === product.originalPrice) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onUpdatePrice?.(product.id, newPrice, finalOrig);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setPriceVal(String(product.price));
      setOrigVal(String(product.originalPrice));
      setEditing(false);
    }
  };

  const hasDiscount = product.originalPrice > product.price;
  const discountPct = hasDiscount
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  if (editing) {
    return (
      <div className="flex flex-col gap-1.5 min-w-[160px]">
        <div className="flex items-center gap-1">
          <div className="relative flex-1">
            <input
              ref={priceRef}
              type="number"
              min="1"
              step="0.01"
              value={priceVal}
              onChange={e => setPriceVal(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="السعر"
              className="w-full text-xs font-semibold border border-[#1a1a1a] rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#1a1a1a] pr-2"
              dir="ltr"
            />
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">ر.س</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="relative flex-1">
            <input
              type="number"
              min="1"
              step="0.01"
              value={origVal}
              onChange={e => setOrigVal(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              placeholder="الأصلي (اختياري)"
              className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-gray-400 text-gray-500 pr-2"
              dir="ltr"
            />
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 pointer-events-none">ر.س</span>
          </div>
          <button
            onMouseDown={e => { e.preventDefault(); handleSave(); }}
            className="w-6 h-6 flex items-center justify-center rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer flex-shrink-0"
            title="حفظ"
          >
            <i className="ri-check-line text-xs"></i>
          </button>
          <button
            onMouseDown={e => {
              e.preventDefault();
              setPriceVal(String(product.price));
              setOrigVal(String(product.originalPrice));
              setEditing(false);
            }}
            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0"
            title="إلغاء"
          >
            <i className="ri-close-line text-xs"></i>
          </button>
        </div>
        <p className="text-[10px] text-gray-400">السطر الثاني: السعر الأصلي قبل الخصم</p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group/price">
      {saving ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
          <i className="ri-loader-4-line animate-spin text-xs"></i>
          جاري الحفظ...
        </span>
      ) : saved ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border bg-emerald-50 text-emerald-600 border-emerald-200">
          <i className="ri-check-line text-xs"></i>
          تم الحفظ
        </span>
      ) : (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-[#1a1a1a] text-sm">
              {product.price.toLocaleString('ar-SA')} ر.س
            </span>
            {hasDiscount && (
              <span className="text-[10px] font-bold bg-red-50 text-red-500 border border-red-100 px-1.5 py-0.5 rounded-full">
                -{discountPct}%
              </span>
            )}
          </div>
          {hasDiscount && (
            <span className="text-[10px] text-gray-400 line-through">
              {product.originalPrice.toLocaleString('ar-SA')} ر.س
            </span>
          )}
        </div>
      )}
      {onUpdatePrice && !saving && !saved && (
        <button
          onClick={() => setEditing(true)}
          className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer opacity-0 group-hover/price:opacity-100 flex-shrink-0"
          title="تعديل السعر"
        >
          <i className="ri-pencil-line text-xs"></i>
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────── ProductTable ─────────────────────────── */
export default function ProductTable({
  products,
  onEdit,
  onDelete,
  onUpdateStock,
  onUpdatePrice,
  onUpdateName,
  onUpdateCategory,
}: ProductTableProps) {
  // pass-through — prevStock is forwarded from StockCell
  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
          <i className="ri-inbox-line text-3xl"></i>
        </div>
        <p className="text-sm">لا توجد منتجات بعد</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" dir="rtl">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-right text-xs font-medium text-gray-400 pb-3 pr-2">المنتج</th>
            <th className="text-right text-xs font-medium text-gray-400 pb-3">الفئة</th>
            <th className="text-right text-xs font-medium text-gray-400 pb-3">السعر</th>
            <th className="text-right text-xs font-medium text-gray-400 pb-3">المخزون</th>
            <th className="text-right text-xs font-medium text-gray-400 pb-3">الصور</th>
            <th className="text-right text-xs font-medium text-gray-400 pb-3">الشارة</th>
            <th className="pb-3"></th>
          </tr>
        </thead>
        <tbody>
          {products.map(product => (
            <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors group">
              {/* Product — inline name editable */}
              <td className="py-3 pr-2">
                <NameCell product={product} onUpdateName={onUpdateName} />
              </td>

              {/* Category — inline editable */}
              <td className="py-3">
                <CategoryCell product={product} onUpdateCategory={onUpdateCategory} />
              </td>

              {/* Price — inline editable */}
              <td className="py-3">
                <PriceCell product={product} onUpdatePrice={onUpdatePrice} />
              </td>

              {/* Stock — inline editable */}
              <td className="py-3">
                <StockCell product={product} onUpdateStock={onUpdateStock} />
              </td>

              {/* Images count */}
              <td className="py-3">
                <div className="flex items-center gap-1">
                  <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-xs font-medium text-gray-600">
                    {product.images?.length || 0}
                  </span>
                  <span className="text-xs text-gray-400">صورة</span>
                </div>
              </td>

              {/* Badge */}
              <td className="py-3">
                {product.badge ? (
                  <span className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-md border border-amber-200">
                    {product.badge}
                  </span>
                ) : (
                  <span className="text-gray-300 text-xs">—</span>
                )}
              </td>

              {/* Actions */}
              <td className="py-3 pl-2">
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onEdit(product)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#1a1a1a] transition-colors cursor-pointer"
                    title="تعديل كامل"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => onDelete(product.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                    title="حذف"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
